import { z } from 'zod'

export const OWN_SERVER_DEFAULTS = {
  imapHost: 'mail.darkember.de',
  imapPort: 993,
  imapSecure: true,
  smtpHost: 'mail.darkember.de',
  smtpPort: 465,
  smtpSecure: true,
  tlsServername: 'mail.darkember.de',
} as const

const requiredText = z.string().trim().min(1).max(320)
// Host names, IPv4/IPv6 literals: no whitespace, no scheme, nothing a DNS resolver would choke on.
const HOST_PATTERN = /^[A-Za-z0-9.:-]+$/
const host = z.string().trim().min(1).max(253).regex(HOST_PATTERN)
const port = z.number().int().min(1).max(65535)
export const mailAccountCreateSchema = z.object({
  label: requiredText,
  email: z.string().trim().email(),
  fromName: z.string().trim().max(320).nullable().optional(),
  imapHost: host,
  imapPort: port.default(OWN_SERVER_DEFAULTS.imapPort),
  imapSecure: z.boolean().default(OWN_SERVER_DEFAULTS.imapSecure),
  imapUser: requiredText,
  imapPassword: z.string().min(1).max(4096),
  smtpHost: host,
  smtpPort: port.default(OWN_SERVER_DEFAULTS.smtpPort),
  smtpSecure: z.boolean().default(OWN_SERVER_DEFAULTS.smtpSecure),
  smtpUser: requiredText,
  smtpPassword: z.string().min(1).max(4096),
  tlsServername: z
    .string()
    .trim()
    .max(253)
    .regex(/^[A-Za-z0-9.-]*$/)
    .nullable()
    .optional(),
})
export type MailAccountInput = z.input<typeof mailAccountCreateSchema>
export interface MailConnectionVerification {
  imap: { ok: boolean; error?: string }
  smtp: { ok: boolean; error?: string }
  durationMs: number
}

// === Sender identities (#839) ================================================
// Per mail account any number of From identities. `own_domain`: an address on
// the own mail server, sent through the account's SMTP. `external_smtp`: an
// address of a foreign domain, sent through that provider's SMTP server with
// its own login. The password is stored encrypted and never leaves the API.

export const MAIL_IDENTITY_KINDS = ['own_domain', 'external_smtp'] as const
export type MailIdentityKind = (typeof MAIL_IDENTITY_KINDS)[number]
/** SMTP ports an external identity may use: 25, submission, SMTPS, 2525. */
export const MAIL_SMTP_ALLOWED_PORTS = [25, 465, 587, 2525] as const
/** IMAP and IMAPS account endpoints (#841). */
export const MAIL_IMAP_ALLOWED_PORTS = [143, 993] as const
export type MailSmtpTls = 'starttls' | 'tls'
export type MailDnsStatus = 'ok' | 'missing' | 'invalid' | 'unknown'

const DNS_LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/
const LOCAL_PART = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/

/** A domain we may resolve and put into From: at least two labels, letters,
 * digits, hyphen (Punycode `xn--` included), no IP literal, at most 253. */
export function isMailIdentityDomain(domain: string): boolean {
  if (domain.length === 0 || domain.length > 253) return false
  const labels = domain.split('.')
  if (labels.length < 2 || !labels.every((label) => DNS_LABEL.test(label))) return false
  // A numeric top-level label would make 10.0.0.1 a "domain".
  return !/^[0-9]+$/.test(labels[labels.length - 1])
}

/** Exactly one bare address, lower case: no display-name syntax, no CR/LF,
 * angle brackets, comma, semicolon, quotes or spaces; at most 254 chars. */
export function isMailIdentityAddress(address: string): boolean {
  if (address.length === 0 || address.length > 254 || address !== address.toLowerCase())
    return false
  const at = address.lastIndexOf('@')
  if (at <= 0 || address.indexOf('@') !== at) return false
  const local = address.slice(0, at)
  return local.length <= 64 && LOCAL_PART.test(local) && isMailIdentityDomain(address.slice(at + 1))
}

const identityAddress = z
  .string()
  .max(254)
  .transform((value) => value.trim().toLowerCase())
  .refine(isMailIdentityAddress, { message: 'Ungültige Adresse' })
// Header injection: no CR/LF or other control characters. Quotes are dropped,
// the address object of the mailer quotes the name itself.
const identityDisplayName = z
  .string()
  .max(200)
  // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what we refuse
  .refine((value) => !/[\u0000-\u001f\u007f]/.test(value), { message: 'Ungültiger Name' })
  .transform((value) => value.replace(/"/g, '').trim() || null)
const smtpHostName = z
  .string()
  .max(253)
  .transform((value) => value.trim().toLowerCase().replace(/\.$/, ''))
  .refine((value) => /^[a-z0-9.:-]+$/.test(value) && value.length > 0, {
    message: 'Ungültiger Server',
  })
// The allowed ports (MAIL_SMTP_ALLOWED_PORTS) are enforced by the API's SMTP
// target guard, together with its host checks and allowlist.
const smtpPort = z.number().int().min(1).max(65535)
const smtpTls = z.enum(['starttls', 'tls'])
const smtpUser = z.string().trim().min(1).max(320)
/** A new password: never empty (an empty string is no delete command). */
const smtpPassword = z.string().min(1).max(4096)

const ownDomainCreate = z
  .object({
    kind: z.literal('own_domain'),
    address: identityAddress,
    displayName: identityDisplayName.nullable().optional(),
    isDefault: z.boolean().optional(),
  })
  .strict()
const externalSmtpCreate = z
  .object({
    kind: z.literal('external_smtp'),
    address: identityAddress,
    displayName: identityDisplayName.nullable().optional(),
    isDefault: z.boolean().optional(),
    smtpHost: smtpHostName,
    smtpPort,
    smtpTls,
    smtpUser,
    smtpPassword,
  })
  .strict()

/** POST /api/mail/accounts/:id/identities */
export const mailIdentityCreateSchema = z.discriminatedUnion('kind', [
  ownDomainCreate,
  externalSmtpCreate,
])
export type MailIdentityCreateInput = z.input<typeof mailIdentityCreateSchema>

/** PATCH /api/mail/accounts/:id/identities/:identityId — `kind` and `address`
 * are immutable (strict: sending them is a 400). No password keeps the stored
 * one; a changed server needs a new password (never send the stored one to a
 * new host). SMTP fields on an own-domain identity are refused by the route. */
export const mailIdentityPatchSchema = z
  .object({
    companyId: z.string().uuid().nullable().optional(),
    signatureMode: z.enum(['auto', 'custom']).optional(),
    signatureGreetingHtml: z.string().max(20_000).nullable().optional(),
    displayName: identityDisplayName.nullable().optional(),
    status: z.enum(['active', 'disabled']).optional(),
    isDefault: z.boolean().optional(),
    smtpHost: smtpHostName.optional(),
    smtpPort: smtpPort.optional(),
    smtpTls: smtpTls.optional(),
    smtpUser: smtpUser.optional(),
    smtpPassword: smtpPassword.optional(),
  })
  .strict()
export type MailIdentityPatchInput = z.input<typeof mailIdentityPatchSchema>

/** POST /api/mail/accounts/:id/identities/test — either unsaved form data
 * (all fields) or `identityId` of a stored external identity; the stored
 * password is only used against the stored server. Never a recipient: the
 * probe stops after MAIL FROM. */
export const mailIdentityTestSchema = z
  .object({
    identityId: z.string().uuid().optional(),
    address: identityAddress.optional(),
    smtpHost: smtpHostName.optional(),
    smtpPort: smtpPort.optional(),
    smtpTls: smtpTls.optional(),
    smtpUser: smtpUser.optional(),
    smtpPassword: smtpPassword.optional(),
  })
  .strict()
export type MailIdentityTestInput = z.input<typeof mailIdentityTestSchema>

/** POST /api/mail/accounts/:id/identities/dns-check — DNS of a not yet saved
 * own-domain address (the form's „Prüfen“ before „Speichern“). */
export const mailIdentityDnsCheckSchema = z.object({ address: identityAddress }).strict()

/** An identity as the API returns it: never the password or its envelope. */
export interface PublicMailIdentity {
  id: string
  accountId: string
  kind: MailIdentityKind
  address: string
  displayName: string | null
  status: 'active' | 'disabled'
  isDefault: boolean
  companyId?: string | null
  signatureMode?: 'auto' | 'custom'
  signatureGreetingHtml?: string | null
  signature?: MailSignaturePreview
  dnsSpf: MailDnsStatus | null
  dnsDkim: MailDnsStatus | null
  dnsDmarc: MailDnsStatus | null
  dnsCheckedAt: string | null
  smtpHost: string | null
  smtpPort: number | null
  smtpTls: MailSmtpTls | null
  smtpUser: string | null
  hasPassword: boolean
  testedAt: string | null
  testOk: boolean | null
  createdAt: string
  updatedAt: string
}

export interface MailSignaturePreview {
  mode: 'auto' | 'custom'
  greetingHtml: string
  requiredLines: Array<{ label: string; value: string; missing: boolean }>
  warning: boolean
  warningLabel: string | null
  plainText: string
}

export const mailCompanySchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    legalForm: z.enum(['gmbh', 'ug', 'ek', 'einzelunternehmen']),
    street: z.string().max(200).nullable().optional(),
    zip: z.string().max(30).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    country: z.string().max(2).default('DE'),
    registerCourt: z.string().max(200).nullable().optional(),
    registerNumber: z.string().max(100).nullable().optional(),
    managingDirectors: z.string().max(1000).nullable().optional(),
  })
  .strict()
export type MailCompanyInput = z.input<typeof mailCompanySchema>
export type MailCompanyPatch = Partial<MailCompanyInput>
export interface PublicMailCompany extends MailCompanyInput {
  id: string
  userId: string
  createdAt: string
  updatedAt: string
}

export const mailTemplateSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    bodyHtml: z.string().max(20_000),
    autoAttachSignature: z.boolean().default(true),
    companyIds: z.array(z.string().uuid()).min(1).max(100),
  })
  .strict()
export type MailTemplateInput = z.input<typeof mailTemplateSchema>
export type MailTemplatePatch = Partial<MailTemplateInput>
export interface PublicMailTemplate {
  id: string
  title: string
  bodyHtml: string
  bodyText: string
  autoAttachSignature: boolean
  companyIds: string[]
  createdAt: string
  updatedAt: string
}

/** One DNS record of the own-domain check with a copyable proposal when it
 * is not ok. The DKIM key is unknown to Embertime: `value` is null there and
 * `hint` points to the Stalwart guide instead. */
export interface MailDnsRecordCheck {
  status: MailDnsStatus
  proposal: { name: string; type: 'TXT'; value: string | null; hint?: string } | null
}
export interface MailIdentityDnsResult {
  domain: string
  spf: MailDnsRecordCheck
  dkim: MailDnsRecordCheck
  dmarc: MailDnsRecordCheck
  checkedAt: string
}

/** Answer of the connection test: fixed fields only, never a server text.
 * `senderAccepted` = the server accepted MAIL FROM (null: not reached). */
export interface MailIdentityTestResult {
  tls: string | null
  auth: 'ok' | 'failed'
  senderAccepted: boolean | null
  error?: MailIdentityTestError
}
export type MailIdentityTestError =
  | 'CONNECT_FAILED'
  | 'TIMEOUT'
  | 'TLS_FAILED'
  | 'STARTTLS_UNAVAILABLE'
  | 'AUTH_FAILED'
  | 'AUTH_UNSUPPORTED'
  | 'SENDER_REFUSED'
  | 'PROTOCOL_ERROR'
